import {Injectable, inject} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {select, Store} from '@ngrx/store';
import {mergeMap, Observable, of, switchMap, withLatestFrom} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {ILocalPageOutputDto} from '@shared/interfaces/orga-response.interface';
import {
  AcceptEditorRequestDto,
  ArticleListDto, CombineOperator,
  DeclineEditorRequestDto,
  FilterDto,
  FilterKind,
  FilterOperators,
  FilterTypes,
  LocationSimpleDto,
  MissionCompleteDto,
  MissionDto,
  MissionReportDto,
  MissionReportPreviewDto,
  MissionState,
  OpenEditorRequestOnContactListDto, Orders,
  ResourceDto,
  ResourceState,
  StereotypeSimpleDto
} from '@server-models';
import {ILocalPagination} from '@shared/interfaces/pagination.interface';
import {TechInventoryApiService} from '@tech/pages/inventory/services/tech-inventory-api.service';
import {TechInventoryBaseEffects} from '@tech/pages/inventory/store/tech-inventory-base.effects';
import {TechInventorySelectors} from '@tech/pages/inventory/store/tech-inventory.selector-type';
import {TechInventoryActions} from '@tech/pages/inventory/store/tech-inventory.actions-type';
import {TranslateService} from '@ngx-translate/core';
import {SharedToastControllerService} from '@shared/services/shared-toast-controller.service';
import {TechInventoryMissionPagesService} from '@tech/pages/inventory/pages/mission-detail/services/tech-mission-pages.service';
import {Router} from "@angular/router";
import {ISharedLocalBodyRequest} from "@shared/interfaces/shared-local-body-request.interface";

@Injectable({
  providedIn: 'root',
})
export class TechInventoryApiEffects extends TechInventoryBaseEffects {
  private _techInventoryApiService = inject(TechInventoryApiService);
  private _router = inject(Router);
  private _toastService = inject(SharedToastControllerService);
  private _translateService = inject(TranslateService);
  private _missionPagesService = inject(TechInventoryMissionPagesService);


  constructor() {
    const actions$ = inject(Actions);
    const store = inject(Store);

    super(store, actions$);
  }

  postMissionPaginated(action$: Observable<ISharedLocalBodyRequest>): Observable<ILocalPageOutputDto<MissionCompleteDto[]> | any> {
    return action$.pipe(
      withLatestFrom(
        this.store.pipe(
          select(TechInventorySelectors.selectTechInventoryPageMissionPagination)
        )
      ),
      switchMap(([action, paginationState]) => {
        if (action.isRefresh) {
          return this._requestPostMissionPaginated(action);
        } else if (this._isLastPagePaginationState(action, paginationState)) {
          return this._requestPostMissionPaginated(action);
        } else {
          return [TechInventoryActions.postMissionPagePaginatedCancel()];
        }
      })
    );
  }

  private _isLastPagePaginationState(action: ISharedLocalBodyRequest, state: ILocalPagination): boolean {
    return !state || action.pagination.pageNumber <= state.totalPages;
  }

  private _requestPostMissionPaginated(
    action: ISharedLocalBodyRequest
  ): Observable<ILocalPageOutputDto<MissionCompleteDto[]> | any> {
    return this._techInventoryApiService
      .pagePostMissionPaginated(action.pagination, action.filters)
      .pipe(
        map((data: ILocalPageOutputDto<MissionCompleteDto[]>) =>
          action.isRefresh
            ? TechInventoryActions.postMissionPagePaginatedRefresh({ data })
            : TechInventoryActions.postMissionPagePaginatedSuccess({ data })
        ),
        catchError((error) => [
          TechInventoryActions.postMissionPagePaginatedFail({ error }),
        ])
      );
  }

  navigateToMissionDetail(action$: Observable<{ missionId: number }>): Observable<any> {
    return action$.pipe(
      tap(({ missionId }) => {
        this._router.navigate([
          `tech/logged/inventory/mission/${ missionId }`
        ]);
      })
    );
  }

  getMissionDetailById(
    action$: Observable<{
      missionId: string;
      existingUrl: string[];
    }>
  ): Observable<any> {
    return action$.pipe(
      mergeMap(({ missionId, existingUrl }) => {
        return this._requestMissionDetailById(
          missionId,
          existingUrl
        );
      })
    );
  }

  private _requestMissionDetailById(
    missionId: string,
    existingUrl: string[]
  ): Observable<any> {
    return this._techInventoryApiService.getMissionDetailById(missionId!).pipe(
      map((data: MissionDto) => {
        return TechInventoryActions.getMissionDetailByIdSuccess({
          data,
          existingUrl,
        });
      }),
      catchError((error) => [
        TechInventoryActions.getMissionDetailByIdFail({ error }),
      ])
    );
  }

  getMissionDetailByIdSuccess(
    action$: Observable<{
      data: MissionDto;
      existingUrl: string[];
    }>
  ): Observable<any> {
    return action$.pipe(
      mergeMap(({ existingUrl }) => {
        const segmentType = this._missionPagesService.getSegmentAndChecK(existingUrl[0]);
        this._missionPagesService.changeSegmentSetting(segmentType);
        return [TechInventoryActions.getMissionDetailByIdDone()];
      })
    );
  }

  setMissionState(
    action$: Observable<{
      id: number;
      stateToChange: MissionState;
      reason: string | null;
    }>
  ): Observable<any> {
    return action$.pipe(
      mergeMap(({ id, stateToChange, reason }) =>
        this._requestSetMissionState({ id, stateToChange, reason })
      )
    );
  }

  private _requestSetMissionState(action: {
    id: number;
    stateToChange: MissionState;
    reason: string | null;
  }): Observable<any> {
    return this._techInventoryApiService
      .postChangeMissionStateById(action.id, action.stateToChange, action.reason)
      .pipe(
        map(() => {
          return TechInventoryActions.setMissionStateSuccess({
            missionState: action.stateToChange,
          });
        }),
        catchError((error) => [
          TechInventoryActions.setMissionStateFail({ error }),
        ])
      );
  }

  setMissionStateSuccess(
    action$: Observable<{ missionState: MissionState }>
  ): Observable<any> {
    return action$.pipe(
      tap(({ missionState }) => {
        return this._toastService.observablePresent({
          message: `${ this._translateService.instant('COMPONENTS.MISSIONS.STATE.TOASTS.' + (missionState ? missionState.toString() : 'DEFAULT')) }`,
        });
      })
    );
  }

  navigateToInventory(action$: Observable<any>): Observable<any> {
    return action$.pipe(
      tap(() => {
        this._router.navigateByUrl('tech/logged/inventory');
      })
    );
  }

  // report
  getMissionReportById(
    action$: Observable<{ reportId: string }>
  ): Observable<any> {
    return action$.pipe(
      mergeMap(({ reportId }) => {
        return this._requestMissionReportById(reportId);
      })
    );
  }

  private _requestMissionReportById(reportId: string): Observable<any> {
    return this._techInventoryApiService.getMissionReportById(reportId).pipe(
      map((data: MissionReportDto) => {
        return TechInventoryActions.getMissionReportByIdSuccess({ data });
      }),
      catchError((error) => [
        TechInventoryActions.getMissionReportByIdFail({ error }),
      ])
    );
  }

  getMissionReportPreviewById(
    action$: Observable<{ missionId: string }>
  ): Observable<any> {
    return action$.pipe(
      mergeMap(({ missionId }) => {
        return this._requestMissionReportPreviewById(missionId);
      })
    );
  }

  private _requestMissionReportPreviewById(missionId: string): Observable<any> {
    return this._techInventoryApiService
      .getMissionReportPreviewById(missionId)
      .pipe(
        map((data: MissionReportPreviewDto) => {
          return TechInventoryActions.getMissionReportPreviewByIdSuccess({
            data,
          });
        }),
        catchError((error) => [
          TechInventoryActions.getMissionReportPreviewByIdFail({ error }),
        ])
      );
  }

  getMissionReportArticleList(action$: Observable<any>): Observable<any> {
    return action$.pipe(
      mergeMap(({ searchTerm, restrictions }) => {
        return this._requestMissionReportArticleList(searchTerm, restrictions);
      })
    );
  }

  private _requestMissionReportArticleList(
    searchTerm?: string,
    restrictions?: StereotypeSimpleDto[]
  ): Observable<any> {
    const body: FilterDto[] = [];

    if (searchTerm?.length) {
      body.push({
        kind: FilterKind.Default,
        type: FilterTypes.DataTransferObject,
        property: 'name',
        operator: FilterOperators.Contains,
        value: searchTerm,
      });
    }

    if (restrictions?.length) {
      body.push({
        type: FilterTypes.Grouped,
        combinedAs: CombineOperator.Or,
        children: restrictions?.map(restriction => ({
          property: 'stereotypeId',
          type: FilterTypes.DataTransferObject,
          operator: FilterOperators.Equal,
          value: restriction.stereotypeId?.toString()
        } as FilterDto)) ?? []
      } as FilterDto)
    }

    return this._techInventoryApiService
      .postMissionReportArticleFilter(body)
      .pipe(
        map((data: ILocalPageOutputDto<ArticleListDto[]>) => {
          return TechInventoryActions.getMissionReportArticleListSuccess({
            data: data.items,
          });
        }),
        catchError((error) => [
          TechInventoryActions.getMissionReportArticleListFail({ error }),
        ])
      );
  }

  submitMissionReport(
    action$: Observable<{
      missionReport: MissionReportDto;
      hasReportId: boolean;
    }>
  ): Observable<any> {
    return action$.pipe(
      mergeMap(({ missionReport, hasReportId }) => {
        return this._submitMissionReport(missionReport, hasReportId);
      })
    );
  }

  private _submitMissionReport(
    missionReport: MissionReportDto,
    hasReportId: boolean
  ): Observable<any> {
    if (hasReportId) {
      return this._techInventoryApiService
        .putSubmitMissionReportById(missionReport)
        .pipe(
          map((data) => {
            return TechInventoryActions.submitMissionReportSuccess({
              response: data,
              hasReportId,
            });
          }),
          catchError((error) => [
            TechInventoryActions.submitMissionReportFail({ error, formData: missionReport }),
          ])
        );
    } else {
      return this._techInventoryApiService
        .postSubmitMissionReport(missionReport)
        .pipe(
          map((data) => {
            return TechInventoryActions.submitMissionReportSuccess({
              response: data,
              hasReportId,
            });
          }),
          catchError((error) => [
            TechInventoryActions.submitMissionReportFail({ error, formData: missionReport }),
          ])
        );
    }
  }

  submitMissionReportSuccess(action$: Observable<any>): Observable<any> {
    return action$.pipe(
      tap(({ hasReportId }) => {
        if (hasReportId) {
          this._toastService.observablePresent({
            message: this._translateService.instant(
              'TECH.INVENTORY.PAGES.MISSIONS.REPORT.ACTIONS.REPORT.FORM.SUCCESS.PUT_MESSAGE'
            ),
          });
        } else {
          this._toastService.observablePresent({
            message: this._translateService.instant(
              'TECH.INVENTORY.PAGES.MISSIONS.REPORT.ACTIONS.REPORT.FORM.SUCCESS.POST_MESSAGE'
            ),
          });
        }
        const urlSegments = this._router.url.split('/');
        urlSegments.push('success');
        void this._router.navigate(urlSegments, {
          state: { isPost: hasReportId },
        });
      })
    );
  }


  postMissionEditorQuestAccept(action$: Observable<{
    missionId: number;
    body: AcceptEditorRequestDto
  }>): Observable<any> {
    return action$.pipe(
      mergeMap(({ missionId, body }) => this._requestPostMissionRequestedAccept(missionId, body))
    );
  }


  private _requestPostMissionRequestedAccept(
    missionId: number,
    body: AcceptEditorRequestDto
  ): Observable<any> {
    return this._techInventoryApiService.postMissionEditorQuestAccept(missionId, body)
      .pipe(
        mergeMap(() => of(TechInventoryActions.postMissionEditorQuestDone({ answer: body, isDeclined: false }))),
        catchError((error) => [
          TechInventoryActions.postMissionEditorQuestAcceptFail({ error }),
        ])
      );
  }

  postMissionEditorQuestDecline(action$: Observable<{
    missionId: number;
    body: DeclineEditorRequestDto
  }>): Observable<any> {
    return action$.pipe(
      mergeMap(({ missionId, body }) => this._requestPostMissionRequestedDecline(missionId, body))
    );
  }

  private _requestPostMissionRequestedDecline(
    missionId: number,
    body: DeclineEditorRequestDto
  ): Observable<any> {
    return this._techInventoryApiService.postMissionEditorQuestDecline(missionId, body)
      .pipe(
        mergeMap(() => of(TechInventoryActions.postMissionEditorQuestDone({ answer: body, isDeclined: true }))),
        catchError((error) => [
          TechInventoryActions.postMissionEditorQuestDeclineFail({ error }),
        ])
      );
  }

  postMissionEditorQuestDone($action: Observable<{
    answer: AcceptEditorRequestDto | DeclineEditorRequestDto,
    isDeclined: boolean
  }>): Observable<any> {
    return $action.pipe(
      mergeMap(({ answer, isDeclined }) => of(
          TechInventoryActions.postMissionRequestedPagePaginated({
            pagination: {
              pageNumber: 1,
              pageSize: 50,
              cols: 'Content',
              sortField: 'mission.plannedStart',
              sort: Number(Orders.Descending),
            },
            filters: [],
            isRefresh: true
          })).pipe(
          tap(() => {
            return this._toastService.observablePresent({
              message: `${ this._translateService.instant(
                isDeclined ? 'TECH.INVENTORY.PAGES.MISSIONS.REQUESTED.DECLINED_SUCCESS' : 'TECH.INVENTORY.PAGES.MISSIONS.REQUESTED.ACCEPTED_SUCCESS'
              ) }`,
            });
          })
        )
      )
    );
  }

  postMissionRequestedPaginated(action$: Observable<ISharedLocalBodyRequest>): Observable<any> {
    return action$.pipe(
      withLatestFrom(
        this.store.pipe(
          select(TechInventorySelectors.selectTechInventoryPageRequestedPagination)
        )
      ),
      switchMap(([action, paginationState]) => {
        if (action.isRefresh) {
          return this._requestPostMissionRequestedPaginated(action);
        } else if (this._isLastPagePaginationState(action, paginationState)) {
          return this._requestPostMissionRequestedPaginated(action);
        } else {
          return [TechInventoryActions.postMissionRequestedPagePaginatedCancel()];
        }
      })
    );
  }

  private _requestPostMissionRequestedPaginated(
    action: ISharedLocalBodyRequest
  ): Observable<ILocalPageOutputDto<OpenEditorRequestOnContactListDto[]> | any> {
    return this._techInventoryApiService
      .postMissionContactMeOpenEditorQuest(action.pagination, action.filters)
      .pipe(
        map((data: ILocalPageOutputDto<OpenEditorRequestOnContactListDto[]>) =>
          action.isRefresh
            ? TechInventoryActions.postMissionRequestedPagePaginatedRefresh({ data })
            : TechInventoryActions.postMissionRequestedPagePaginatedSuccess({ data })
        ),
        catchError((error) => [
          TechInventoryActions.postMissionRequestedPagePaginatedFail({ error }),
        ])
      );
  }

  // resource
  navigateToResourceDetail(action$: Observable<{ resourceId: any }>): Observable<any> {
    return action$.pipe(
      tap(({ resourceId }) => {
        // can be related to this._activatedRoute
        this._router.navigate([`tech/logged/inventory/mission/${ resourceId }`]);
      })
    );
  }

  getResourceDetailById(
    action$: Observable<{
      resourceId: string;
      existingUrl: string[];
    }>
  ): Observable<any> {
    return action$.pipe(
      mergeMap(({ resourceId, existingUrl }) => {
        return this._requestResourceDetailById(
          resourceId,
          existingUrl
        );
      })
    );
  }

  private _requestResourceDetailById(
    resourceId: string,
    existingUrl: string[]
  ): Observable<any> {
    return this._techInventoryApiService.getResourceDetailById(resourceId).pipe(
      map((data: ResourceDto) => {
        return TechInventoryActions.getResourceDetailByIdSuccess({
          data,
          existingUrl,
        });
      }),
      catchError((error) => {
        return [TechInventoryActions.getResourceDetailByIdFail({ error })]
      })
    );
  }

  setResourceState(
    action$: Observable<{ resourceId: number; state: ResourceState }>
  ): Observable<any> {
    return action$.pipe(
      mergeMap(({ resourceId, state }) =>
        this._techInventoryApiService.postChangeResourceStateById(resourceId, state).pipe(
          map(() => TechInventoryActions.setResourceStateSuccess({ resourceId, state })),
          catchError((error) => [TechInventoryActions.setResourceStateFail({ resourceId, error })])
        )
      )
    );
  }

  setResourceStateSuccess(
    action$: Observable<{ resourceId: number; state: ResourceState }>
  ): Observable<any> {
    return action$.pipe(
      tap(() => this._toastService.observablePresent({
        message: this._translateService.instant('TECH.INVENTORY.PAGES.RESOURCES.DETAILS.CHANGE_STATE.CHANGED'),
      }))
    );
  }

  setResourceLocation(
    action$: Observable<{ resourceId: number; state: ResourceState; location: LocationSimpleDto }>
  ): Observable<any> {
    return action$.pipe(
      mergeMap(({ resourceId, state, location }) =>
        this._techInventoryApiService.postChangeResourceLocationById(resourceId, state, location.locationId!).pipe(
          map(() => TechInventoryActions.setResourceLocationSuccess({ resourceId, location })),
          catchError((error) => [TechInventoryActions.setResourceLocationFail({ resourceId, error })])
        )
      )
    );
  }

  setResourceLocationSuccess(
    action$: Observable<{ resourceId: number; location: LocationSimpleDto }>
  ): Observable<any> {
    return action$.pipe(
      tap(() => this._toastService.observablePresent({
        message: this._translateService.instant('TECH.INVENTORY.PAGES.RESOURCES.DETAILS.CHANGE_LOCATION.CHANGED'),
      }))
    );
  }

  getResourceDetailByIdMissionsPaginated(action$: Observable<{
    request: ISharedLocalBodyRequest,
    resourceId: number
  }>): Observable<ILocalPageOutputDto<MissionCompleteDto[]> | any> {
    return action$.pipe(
      withLatestFrom(
        this.store.pipe(select(TechInventorySelectors.selectTechInventoryPageResource))
      ),
      mergeMap(([{ request, resourceId }, resourceModel]) => {
        const paginationState = resourceModel.missions.paging;
        if (request.isRefresh) {
          return this._requestGetResourceDetailByIdMissionsPaginated(request, resourceId!);
        } else if (this._isLastPagePaginationState(request, paginationState)) {
          return this._requestGetResourceDetailByIdMissionsPaginated(request, resourceId!);
        } else {
          return [TechInventoryActions.getResourceDetailByIdMissionsPaginatedCancel()];
        }
      })
    );
  }

  private _requestGetResourceDetailByIdMissionsPaginated(request: ISharedLocalBodyRequest,
                                                         resourceId: number): Observable<ILocalPageOutputDto<MissionCompleteDto[]> | any> {
    return this._techInventoryApiService
      .getResourceDetailByIdMissions(request, resourceId)
      .pipe(
        map((data: ILocalPageOutputDto<MissionCompleteDto[]>) =>
          request.isRefresh
            ? TechInventoryActions.getResourceDetailByIdMissionsPaginatedRefresh({ data })
            : TechInventoryActions.getResourceDetailByIdMissionsPaginatedSuccess({ data })
        ),
        catchError((error) => [
          TechInventoryActions.getResourceDetailByIdMissionsPaginatedFail({ error }),
        ])
      );
  }

  override getArticleRestrictions(action$: Observable<{
    missionId: number, stereotypeId: number
  }>): Observable<StereotypeSimpleDto[]> {
    return action$.pipe(
      mergeMap(({ stereotypeId, missionId }) =>
        this._techInventoryApiService.getArticleRestrictions(missionId, stereotypeId).pipe(
          map((restrictions) => TechInventoryActions.getArticleRestrictionsSuccess({ restrictions })),
          catchError((error) => of(error))
        ))
    );
  }

  replanMission$ = createEffect(() => this.actions().pipe(
      ofType(TechInventoryActions.replanMission),
      action => this.replanMission(action)),
    { dispatch: true }
  );

  private replanMission(action$: Observable<{ missionId: number; actualStart: string; actualEnd: string }>) {
    return action$.pipe(
      mergeMap(({ missionId, actualStart, actualEnd }) => this._techInventoryApiService.replanMission(missionId, {
        actualStart, actualEnd
      }).pipe(
        mergeMap(() => of(TechInventoryActions.replanMissionSuccess())),
        catchError((error) => [
          TechInventoryActions.replanMissionFail({ error }),
        ])
      ))
    );
  }

}
